// Client-side image prep for uploads.
//
// Phone originals are 12MB+ and iPhone/Android photos carry an EXIF orientation
// tag. Hand-rolling the rotation is a trap: createImageBitmap's default EXIF
// handling differs per engine/version, so a manual transform double-rotates on
// one browser and is correct on another. Instead we ask the browser to apply
// the orientation for us ("from-image") and only handle resizing — the browser
// hands back upright pixels and correct width/height. Supported on current iOS
// Safari and Android Chrome.

const MAX_EDGE = 1200;
const JPEG_QUALITY = 0.8;

/**
 * Resize to a max longest edge and export JPEG, with EXIF orientation applied
 * by the browser. Test on a phone — orientation bugs only show on real photos.
 */
export async function prepareUpload(
  file: File,
): Promise<{ blob: Blob; contentType: "image/jpeg" }> {
  // "from-image" → browser applies EXIF orientation; bitmap dims are already
  // the upright display dimensions.
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });

  const longest = Math.max(bitmap.width, bitmap.height);
  const scale = longest > MAX_EDGE ? MAX_EDGE / longest : 1;
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    throw new Error("canvas 2d context unavailable");
  }

  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
  );
  if (!blob) throw new Error("failed to encode image");
  return { blob, contentType: "image/jpeg" };
}

/** PUT a blob to a presigned URL with upload progress. Signature is the auth. */
export function uploadToPresigned(
  url: string,
  blob: Blob,
  contentType: string,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error("upload failed"));
    xhr.send(blob);
  });
}
